import React, { useEffect } from 'react';
import ActionButton from '~components/atoms/ActionButton';
import Table from '~components/molecules/Table';
import UtilFunctions from '~utils/functions';
import { useAppDispatch, useAppSelector } from '~redux/hooks';
import { ModuleEnum, PermissionsEnum, TransactionActionEnum, TransactionStatusEnum } from '~utils/enums';

import * as Styles from './_discountApprovalTableStyles.module.scss';
import { TPendingDiscountListing, TUpdateDiscountStatusReduxParams } from '~types/feat/approval';
import combinedActions from '~redux/combinedActions';

const DiscountApprovalTable = () => {
    const dispatch = useAppDispatch();

    const {
        Misc:
        {
            dateTimeFormat,
            renderTransactionStatusChip,
        },
    } = UtilFunctions;

    const discountListing = useAppSelector(state => state.approval.pendingDiscountListing.data);
    const getPendingDiscountIsLoading = useAppSelector(state => state.approval.loadings.getPendingDiscountListing);
    const isMarketOpen = useAppSelector(state => state.auth.isMarketOpen);

    const access = useAppSelector(state => state.auth.userDetails.access);
    const hasEditAccess = access.find(item => item.permission === PermissionsEnum.Edit)?.modules.find(module => module === ModuleEnum.Approval);

    const setTempUpdateTransactionState = (data: TUpdateDiscountStatusReduxParams) => dispatch(combinedActions.approvalSetTempUpdateDiscountState(data));
    const setConfirmUpdateStatusModalIsOpen = (open: boolean) => dispatch(combinedActions.approvalSetConfirmUpdateDiscountStatusModalIsOpen(open));

    const handleApproveClick = (data: TUpdateDiscountStatusReduxParams) => {
        setConfirmUpdateStatusModalIsOpen(true);
        setTempUpdateTransactionState(data);
    };

    const handleRejectClick = (data: TUpdateDiscountStatusReduxParams) => {
        setConfirmUpdateStatusModalIsOpen(true);
        setTempUpdateTransactionState(data);
    };

    const tableHeader = [
        'DISCOUNT CODE',
        'DISCOUNT NAME',
        'AMOUNT',
        'DISCOUNT TYPE',
        'STATUS',
        'EXPIRY DATE',
        'ACTION',
    ];

    const renderActionButton = (id: number, status:TransactionStatusEnum ) => {
        if (status === TransactionStatusEnum.Pending){
            return (
                <div
                    className={Styles['action-button-container']}
                >
                    {hasEditAccess
                    && (
                        <ActionButton
                            label='Approve'
                            onClick={() => handleApproveClick({ id, status: TransactionStatusEnum.Approved })}
                            className={`${Styles['action-button']} ${Styles.approve}`}
                        />
                    )
                    }

                    {hasEditAccess
                    && (
                        <ActionButton
                            label='Reject'
                            onClick={() => handleRejectClick({ id, status: TransactionStatusEnum.Rejected })}
                            className={`${Styles['action-button']} ${Styles.reject}`}
                        />
                    )
                    }
                </div>
            );
        }

        return '-';

    };

    const tableBody = discountListing.map((item, index) => {
        const {
            id,
            discountCode,
            discountName,
            amount,
            percentageAmount,
            transactionType,
            status,
            discountEndDate,
        } = item;

        const renderTransactionType = () => {
            if (transactionType === TransactionActionEnum['Bank Sell']){
                return (
                    <div>
                        <p>Discount</p>
                        <p>({TransactionActionEnum[transactionType]})</p>
                    </div>
                );
            }

            if (transactionType === TransactionActionEnum['Bank Buy']){
                return (
                    <div>
                        <p>Markup</p>
                        <p>({TransactionActionEnum[transactionType]})</p>
                    </div>
                );
            }
        };

        const handleCalculateValue = (fixedValue: string | undefined, percentageValue: number | undefined) => {
            if (fixedValue){
                const shifted = ((parseFloat(fixedValue) * 100) / 100);

                if (Number.isInteger(shifted * 100)) {
                    return `RM${shifted.toFixed(2)}`;
                }

                return `RM${parseFloat(shifted.toFixed(6)).toString()}`;
            }

            if (percentageValue){
                const shifted = percentageValue * 100;

                if (Number.isInteger(shifted * 100)) {
                    return shifted.toFixed(2).concat('%');
                }

                return parseFloat(shifted.toFixed(6)).toString().concat('%');
            }

            return '-';
        };

        return [
            discountCode,
            discountName,
            handleCalculateValue(amount, percentageAmount),
            renderTransactionType(),
            renderTransactionStatusChip(status, TransactionStatusEnum[status]),
            (
                <div
                    key={index}
                    className={Styles['discount-type-date']}
                >
                    {dateTimeFormat(discountEndDate)}
                </div>
            ),
            renderActionButton(id, status),
        ];
    });

    return (
        <Table
            header={tableHeader}
            body={tableBody}
            isLoading={getPendingDiscountIsLoading}
        />
    );
};

export default DiscountApprovalTable;
